import { formatDate } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GetMaintenanceResponse, MaintenanceStatus, MiscDataService, UpdateClientAcceptedTermsRequestStatusEnum } from '@tecex-api/data';
import isNil from 'lodash/isNil';
import { asyncScheduler, BehaviorSubject, combineLatest, EMPTY, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, mapTo, observeOn, switchMap } from 'rxjs/operators';
import { ErrorNotificationService } from '../../../services/error-notification.service';
import { DialogService } from '../../dialog/dialog.service';
import { MaintenanceDialogPayload } from '../components/maintenance-dialog/maintenance-dialog-payload.interface';
import { MaintenanceDialogComponent } from '../components/maintenance-dialog/maintenance-dialog.component';
import { AnnouncmentType } from '../enums/announcment-type.enum';
import { ANNOUNCMENT_CLOSED, TERMS_CLOSED } from '../helpers/announcment-closed-session-storage.helper';
import { Announcment } from '../types/announcment.type';
import { AuthService } from '../../../services/auth.service';
import { TermsAndConditionsDialogComponent } from '../components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { CONFIG_TOKEN } from '../../../config/config.token';
import { GlobalConfig } from '../../../config/global-config.interface';
import { LoadingIndicatorService } from '../../loading-indicator/services/loading-indicator.service';
import { TokenConfigService } from '../../../services/token-config.service';
import { HttpStatusCode } from '@angular/common/http';
import { termsAndConditionsDialogPayload } from '../components/terms-and-conditions-dialog/terms-and-conditions-dialog-payload.interface';

export const initializeAnnouncment = () => (announcmentService: AnnouncmentService) => (): Promise<void> => announcmentService.initialize();

@Injectable({
  providedIn: 'root',
})
export class AnnouncmentService {
  public announcment$ = new BehaviorSubject<null | Announcment>(null);

  private readonly announcmentClosed;

  constructor(
    private readonly miscDataService: MiscDataService,
    private readonly errorNotificationService: ErrorNotificationService,
    private readonly translateService: TranslateService,
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly dialogService: DialogService,
    @Inject(CONFIG_TOKEN) private readonly config: GlobalConfig,
    private readonly authService: AuthService,
    private readonly loadingIndicatorService: LoadingIndicatorService,
    private readonly tokenConfigService: TokenConfigService
  ) {
    this.announcmentClosed = JSON.parse(sessionStorage.getItem(ANNOUNCMENT_CLOSED));
  }

  public handleMaintenance(): void {
    this.announcment$
      .pipe(
        switchMap((announcment) => {
          if (isNil(announcment) || announcment.type === AnnouncmentType.Status) {
            return EMPTY;
          }

          // tslint:disable-next-line: no-magic-numbers
          const millisecondsUntilMaintenance = Math.floor(new Date(announcment.startDate).getTime() - Date.now());

          if (millisecondsUntilMaintenance < 0) {
            return of(announcment);
          }

          return timer(millisecondsUntilMaintenance).pipe(mapTo(announcment));
        }),
        observeOn(asyncScheduler),
        switchMap((announcment) =>
          this.dialogService
            .open<MaintenanceDialogPayload>(
              MaintenanceDialogComponent,
              { announcment },
              { closeButton: false, closeOnBackdropClick: false }
            )
            .afterClosed$()
        )
      )
      .subscribe();
  }

  public async initialize(): Promise<void> {
    try {
      const maintenance = await this.miscDataService.getMaintenance().toPromise();
      this.evaluateMaintenance(maintenance);
      const tokenUser$ = combineLatest([this.tokenConfigService.getTokens$(), this.authService.getUser$()]);
      tokenUser$.subscribe((response) => {
        // We need to check if the revision number exists,
        // otherwise it could potentially cause production issues without an easy way to revert
        // This way we can remove it form the config file and redeploy to disable the dialog
        if (response[0].termsRevisionId && response[1].latestTermsAccepted !== response[0].termsRevisionId) {
          // if(false){
          //Need to access if component will be dismissable
          const gracePeriod = Number.parseInt(this.config.conditionCutoff) - Math.trunc(Date.now() / 1000) > 0;
          if ((gracePeriod && !JSON.parse(sessionStorage.getItem(TERMS_CLOSED))) || !gracePeriod) {
            this.dialogService.open<termsAndConditionsDialogPayload>(
              TermsAndConditionsDialogComponent,
              { gracePeriod },
              {
                closeButton: gracePeriod,
                closeOnBackdropClick: gracePeriod,
                closeOnNavigation: false,
                preventScrollOffsetSaving: true,
                closeOnBrowserBackForward: gracePeriod,
              }
            );
          }
        }
      });
    } catch (error: any) {
      if (error.status !== HttpStatusCode.Unauthorized && error.status !== 0) {
        await this.errorNotificationService.notifyAboutError(error, 'ERROR.FAILED_TO_LOAD_ANNOUNCMENT');
      }
    }
  }

  public evaluateMaintenance(maintenance: GetMaintenanceResponse): Promise<void> | void {
    // Below checks if there is either no Maintenance or if there is only a banner
    // This is the only time when we should display the Terms and conditions
    const showStatus = isNil(maintenance.Success.Schedulestarttime) && isNil(maintenance.Success.ScheduleendtimeUTC);
    if (isNil(maintenance.Success?.Id) || showStatus) {
      this.authService.getUser$().subscribe((user) => {
        // No empty functions
      });
    }

    if (isNil(maintenance.Success?.Id)) {
      return;
    }

    if (isNil(maintenance.Success.Schedulestarttime) && isNil(maintenance.Success.ScheduleendtimeUTC)) {
      return this.announcment$.next({
        type: AnnouncmentType.Status,
        message: maintenance.Success.DisplayMessage,
      });
    }

    if (
      ![MaintenanceStatus.ACTIVE, MaintenanceStatus.ON_GOING].includes(maintenance.Success?.Status) ||
      new Date(maintenance.Success.ScheduleendtimeUTC) < new Date()
    ) {
      return;
    }

    this.announcment$.next({
      type: AnnouncmentType.Maintenance,
      message: maintenance.Success.DisplayMessage,
      startDate: maintenance.Success.Schedulestarttime,
      endDate: maintenance.Success.ScheduleendtimeUTC,
      status: maintenance.Success.Status,
    });
  }

  public isUnderMaintenance$(): Observable<boolean> {
    return this.announcment$.pipe(
      map((announcment) => {
        if (isNil(announcment)) {
          return false;
        }

        return announcment.type === AnnouncmentType.Maintenance && announcment.status === MaintenanceStatus.ON_GOING;
      })
    );
  }

  // tslint:disable: no-magic-numbers no-null-keyword
  public getMessage$(): Observable<null | string> {
    return this.announcment$.pipe(
      switchMap((announcment) => {
        if (this.announcmentClosed) {
          return of(null);
        }

        if (isNil(announcment)) {
          return of(null);
        }

        if (announcment.type === AnnouncmentType.Status) {
          return of(announcment.message);
        }

        return timer(0, 1000).pipe(
          map(() => {
            const differenceInSeconds = Math.floor((new Date(announcment.startDate).getTime() - Date.now()) / 1000);

            if (differenceInSeconds < 0) {
              return null;
            }

            const remainingMinutes = Math.floor(differenceInSeconds / 60);
            const remainingSeconds = differenceInSeconds % 60;

            const date = formatDate(announcment.startDate, 'dd LLL, yyyy', this.locale);
            const from = formatDate(announcment.startDate, 'hh:mm aaa', this.locale);
            const to = formatDate(announcment.endDate, 'hh:mm aaa', this.locale);
            const fromTo = this.translateService.instant('MAINTENANCE.FROM_TO', { from, to });

            const message = `${announcment.message} ${date} ${fromTo}.`;

            if (remainingMinutes > 60) {
              return message;
            }

            const countdown = `${remainingMinutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
            const translatedCountdown = this.translateService.instant('MAINTENANCE.COUNTDOWN', {
              countdown,
            });

            return `${message} ${translatedCountdown}`;
          })
        );
      }),
      distinctUntilChanged()
    );
  }

  public closeAnnouncment(): void {
    sessionStorage.setItem(ANNOUNCMENT_CLOSED, JSON.stringify(true));
    this.announcment$.next(null);
  }

  public rejectTerms$() {
    this.loadingIndicatorService.open();
    const tokenUser$ = combineLatest([this.tokenConfigService.getTokens$(), this.authService.getUser$()]);
    return tokenUser$.pipe(
      switchMap((response) => {
        return this.miscDataService
          .updateClientAcceptedTerms({
            AccessToken: response[1].accessToken,
            Contact: response[1].contactId,
            Status: UpdateClientAcceptedTermsRequestStatusEnum.REJECTED,
            Version: response[0].termsRevisionId,
          })
          .pipe(
            map(() => {
              return this.authService.logout();
            })
          );
      })
    );
  }

  public acceptTerms$() {
    this.loadingIndicatorService.open();
    const tokenUser$ = combineLatest([this.tokenConfigService.getTokens$(), this.authService.getUser$()]);
    return tokenUser$.pipe(
      switchMap((response) => {
        return this.miscDataService
          .updateClientAcceptedTerms({
            AccessToken: response[1].accessToken,
            Contact: response[1].contactId,
            Status: UpdateClientAcceptedTermsRequestStatusEnum.ACCEPTED,
            Version: response[0].termsRevisionId,
          })
          .pipe(
            map(() => {
              this.loadingIndicatorService.dispose();
            })
          );
      })
    );
  }
}
